import { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  checkPaymentCompletionQuery,
  checkPaymentCompletionQueryVariables,
  checkPaymentSetupCompletionQuery,
  checkPaymentSetupCompletionQueryVariables,
  PaymentDocumentType
} from '../../core-types';
import { captureException } from '../../lib/sentry';
import {
  CHECK_PAYMENT_COMPLETION_QUERY,
  CHECK_PAYMENT_SETUP_COMPLETION_QUERY
} from '../../lib/core/asyncProcessCompletion';

export type PaymentState = 'success' | 'cancelled';

export type PostMessagePaymentState =
  | 'success'
  | 'cancelled'
  | 'failed'
  | 'error';

const notifyOnNetworkStatusChange = true;
const fetchPolicy = 'no-cache';

interface CommonOptions {
  skip?: boolean;
  paymentState: PaymentState;
  onProcess?: () => void;
  onStateChange?: (state: PostMessagePaymentState) => void;
  onAsyncProcessCompleted?: (state: PostMessagePaymentState) => void;
  maxAttempts?: number;
}

interface AsyncProcessResult {
  loading: boolean;
  postMessageState?: PostMessagePaymentState;
}

function handleTypename(
  typename: string,
  startPolling: ((ms: number) => void) | undefined,
  checkAfterMilliseconds: number | undefined,
  setPostMessageState: (state: PostMessagePaymentState) => void
) {
  switch (typename) {
    case 'AsyncProcessCompletionPending': {
      if (startPolling && checkAfterMilliseconds) {
        startPolling(checkAfterMilliseconds);
      }
      break;
    }
    case 'AsyncProcessCompleted': {
      setPostMessageState('success');
      break;
    }
    case 'AsyncProcessFailed':
      setPostMessageState('failed');
      break;
    // FIXME: figure out how to handle this case https://urbansharing.slack.com/archives/C808DKWTH/p1607931978055800
    case 'AsyncProcessCompletionPostponed': {
      setPostMessageState('failed');
      break;
    }
  }
}

export function usePaymentCompletion({
  skip,
  paymentState,
  token,
  type,
  onProcess,
  onStateChange,
  onAsyncProcessCompleted,
  maxAttempts = 20
}: CommonOptions & {
  token?: string;
  type?: PaymentDocumentType;
}): AsyncProcessResult {
  const [attempts, setAttempts] = useState(0);
  const [postMessageState, setPostMessageState] = useState<
    PostMessagePaymentState | undefined
  >(paymentState === 'cancelled' ? paymentState : undefined);

  const [execute, result] = useLazyQuery<
    checkPaymentCompletionQuery,
    checkPaymentCompletionQueryVariables
  >(CHECK_PAYMENT_COMPLETION_QUERY, {
    ssr: false,
    fetchPolicy,
    notifyOnNetworkStatusChange,
    onCompleted: data => {
      setAttempts(prev => prev + 1);
      if (!data) {
        return;
      }

      handleTypename(
        data.checkPaymentCompletion.__typename,
        result.startPolling,
        data.checkPaymentCompletion.__typename ===
          'AsyncProcessCompletionPending'
          ? data.checkPaymentCompletion.checkAfterMilliseconds
          : undefined,
        setPostMessageState
      );
    },
    onError: () => {
      setAttempts(prev => prev + 1);
    }
  });

  useEffect(() => {
    if (!skip && token && type && paymentState !== 'cancelled') {
      execute({ variables: { token, type } });
    }
  }, [skip, token, type, paymentState, execute]);

  useEffect(() => {
    if (!postMessageState) {
      return;
    }

    onAsyncProcessCompleted?.(postMessageState);
    onStateChange?.(postMessageState);
    result.stopPolling?.();
  }, [postMessageState]);

  useEffect(() => {
    if (skip) {
      return;
    }

    if (attempts > maxAttempts) {
      setPostMessageState('error');
      captureException(new Error('AsyncProcessCompletion timed out'), {
        paymentState,
        token,
        type
      });
    } else if (attempts > 0 && onProcess) {
      onProcess();
    }
  }, [onProcess, attempts, maxAttempts, skip]);

  if (typeof window === 'undefined') {
    return { loading: false, postMessageState };
  }

  return {
    loading: result.called && !postMessageState,
    postMessageState
  };
}

export function usePaymentSetupCompletion({
  skip,
  paymentState,
  setupToken,
  onProcess,
  onStateChange,
  onAsyncProcessCompleted,
  maxAttempts = 20
}: CommonOptions & {
  setupToken?: string;
}): AsyncProcessResult {
  const [attempts, setAttempts] = useState(0);
  const [postMessageState, setPostMessageState] = useState<
    PostMessagePaymentState | undefined
  >(paymentState === 'cancelled' ? paymentState : undefined);

  const [execute, result] = useLazyQuery<
    checkPaymentSetupCompletionQuery,
    checkPaymentSetupCompletionQueryVariables
  >(CHECK_PAYMENT_SETUP_COMPLETION_QUERY, {
    ssr: false,
    fetchPolicy,
    notifyOnNetworkStatusChange,
    onCompleted: data => {
      setAttempts(prev => prev + 1);
      if (!data) {
        return;
      }

      handleTypename(
        data.checkPaymentSetupCompletion.__typename,
        result.startPolling,
        data.checkPaymentSetupCompletion.__typename ===
          'AsyncProcessCompletionPending'
          ? data.checkPaymentSetupCompletion.checkAfterMilliseconds
          : undefined,
        setPostMessageState
      );
    },
    onError: () => {
      setAttempts(prev => prev + 1);
    }
  });

  useEffect(() => {
    if (!skip && setupToken && paymentState !== 'cancelled') {
      execute({ variables: { setupToken } });
    }
  }, [skip, setupToken, paymentState, execute]);

  useEffect(() => {
    if (!postMessageState) {
      return;
    }

    onAsyncProcessCompleted?.(postMessageState);
    onStateChange?.(postMessageState);
    result.stopPolling?.();
  }, [postMessageState]);

  useEffect(() => {
    if (skip) {
      return;
    }

    if (attempts > maxAttempts) {
      setPostMessageState('error');
      captureException(new Error('AsyncProcessCompletion timed out'), {
        paymentState,
        setupToken
      });
    } else if (attempts > 0 && onProcess) {
      onProcess();
    }
  }, [onProcess, attempts, maxAttempts, skip]);

  if (typeof window === 'undefined') {
    return { loading: false, postMessageState };
  }

  return {
    loading: result.called && !postMessageState,
    postMessageState
  };
}
